import React, { useState } from "react"

import * as Sentry from "@sentry/react"
import { Loader2 } from "lucide-react"

import { TabsContent } from "@/components/ui/tabs"

import CPCSection from "./document-sections/CPCSection"
import D4MedicalSection from "./document-sections/D4MedicalSection"
import DVLASection from "./document-sections/DVLASection"
import InsuranceSection from "./document-sections/InsuranceSection"
import RTWSection from "./document-sections/RTWSection"
import TachoCardSection from "./document-sections/TachoCardSection"
import DocumentUploadTabs from "./document-upload/DocumentUploadTabs"
import { useDocumentSubmissionHandlers } from "./document-upload/useDocumentSubmissionHandlers"
import { useDocumentUploadState } from "./document-upload/useDocumentUploadState"

const DocumentUpload: React.FC = () => {
  const state = useDocumentUploadState()

  // Track loading states for each document section
  const [isDVLALoading, setIsDVLALoading] = useState(false)
  const [isRTWLoading, setIsRTWLoading] = useState(false)
  const [isInsuranceLoading, setIsInsuranceLoading] = useState(false)
  const [isCPCLoading, setIsCPCLoading] = useState(false)
  const [isTachoLoading, setIsTachoLoading] = useState(false)
  const [isD4Loading, setIsD4Loading] = useState(false)

  const handlers = useDocumentSubmissionHandlers({
    // DVLA
    drivingLicenceNumber: state.drivingLicenceNumber,
    licenseFrontFile: state.licenseFrontFile,
    licenseFrontPreview: state.licenseFrontPreview,
    licenseBackFile: state.licenseBackFile,
    licenseBackPreview: state.licenseBackPreview,
    dvlaAttempts: state.dvlaAttempts,
    setDvlaAttempts: state.setDvlaAttempts,
    // RTW
    rtwShareCode: state.rtwShareCode,
    documentFile: state.documentFile,
    documentPreview: state.documentPreview,
    rtwAttempts: state.rtwAttempts,
    setRtwAttempts: state.setRtwAttempts,
    // Insurance
    insuranceProvider: state.insuranceProvider,
    policyNumber: state.policyNumber,
    insuranceProofFile: state.insuranceProofFile,
    insuranceProofPreview: state.insuranceProofPreview,
    // CPC
    cpcFrontFile: state.cpcFrontFile,
    cpcBackFile: state.cpcBackFile,
    cpcFrontPreview: state.cpcFrontPreview,
    cpcBackPreview: state.cpcBackPreview,
    // Tacho
    tachoFrontFile: state.tachoFrontFile,
    tachoBackFile: state.tachoBackFile,
    tachoFrontPreview: state.tachoFrontPreview,
    tachoBackPreview: state.tachoBackPreview,
    tachoCardNumber: state.tachoCardNumber,
    // D4
    d4MedicalFile: state.d4MedicalFile,
    d4MedicalPreview: state.d4MedicalPreview,
    // App state
    setApplication: state.setApplication,
    setSubmittedTabs: state.setSubmittedTabs,
    setActiveTab: state.setActiveTab,
  })

  // Wrap handlers to manage loading states
  const handleDVLASubmit = async () => {
    setIsDVLALoading(true)
    try {
      await handlers.handleDVLASubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleDVLASubmit",
          documentType: "DVLA",
        },
      })
    } finally {
      setIsDVLALoading(false)
    }
  }

  const handleCPCSubmit = async () => {
    setIsCPCLoading(true)
    try {
      await handlers.handleCPCSubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleCPCSubmit",
          documentType: "CPC",
        },
      })
    } finally {
      setIsCPCLoading(false)
    }
  }

  const handleTachoCardSubmit = async () => {
    setIsTachoLoading(true)
    try {
      await handlers.handleTachoCardSubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleTachoCardSubmit",
          documentType: "TachoCard",
        },
      })
    } finally {
      setIsTachoLoading(false)
    }
  }

  const handleD4MedicalSubmit = async () => {
    setIsD4Loading(true)
    try {
      await handlers.handleD4MedicalSubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleD4MedicalSubmit",
          documentType: "D4Medical",
        },
      })
    } finally {
      setIsD4Loading(false)
    }
  }

  const handleRTWSubmit = async () => {
    setIsRTWLoading(true)
    try {
      await handlers.handleRTWSubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleRTWSubmit",
          documentType: "RTW",
        },
      })
    } finally {
      setIsRTWLoading(false)
    }
  }

  const handleInsuranceSubmit = async () => {
    setIsInsuranceLoading(true)
    try {
      await handlers.handleInsuranceSubmit()
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "DocumentUpload.handleInsuranceSubmit",
          documentType: "Insurance",
        },
      })
    } finally {
      setIsInsuranceLoading(false)
    }
  }

  const handleTabChange = (value: string) => {
    state.setActiveTab(value)
  }

  if (state.isLoading) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center justify-center">
        <Loader2 className="mb-4 h-12 w-12 animate-spin text-honeycomb" />
        <h2 className="mb-2 text-xl font-semibold">Loading...</h2>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl">
      <DocumentUploadTabs
        activeTab={state.activeTab}
        onTabChange={handleTabChange}
        submittedTabs={state.submittedTabs}
      >
        <TabsContent value="dvla-check">
          <DVLASection
            drivingLicenceNumber={state.drivingLicenceNumber}
            setdrivingLicenceNumber={state.setdrivingLicenceNumber}
            licenseFrontFile={state.licenseFrontFile}
            setLicenseFrontFile={state.setLicenseFrontFile}
            licenseFrontPreview={state.licenseFrontPreview}
            setLicenseFrontPreview={state.setLicenseFrontPreview}
            licenseBackFile={state.licenseBackFile}
            setLicenseBackFile={state.setLicenseBackFile}
            licenseBackPreview={state.licenseBackPreview}
            setLicenseBackPreview={state.setLicenseBackPreview}
            onSubmit={handleDVLASubmit}
            isLoading={isDVLALoading}
          />
        </TabsContent>

        <TabsContent value="rtw-check">
          <RTWSection
            rtwShareCode={state.rtwShareCode}
            setRtwShareCode={state.setRtwShareCode}
            documentFile={state.documentFile}
            setDocumentFile={state.setDocumentFile}
            documentPreview={state.documentPreview}
            setDocumentPreview={state.setDocumentPreview}
            onSubmit={handleRTWSubmit}
            isLoading={isRTWLoading}
          />
        </TabsContent>

        <TabsContent value="insurance-check">
          <InsuranceSection
            insuranceProvider={state.insuranceProvider}
            setInsuranceProvider={state.setInsuranceProvider}
            policyNumber={state.policyNumber}
            setPolicyNumber={state.setPolicyNumber}
            insuranceProofFile={state.insuranceProofFile}
            setInsuranceProofFile={state.setInsuranceProofFile}
            insuranceProofPreview={state.insuranceProofPreview}
            setInsuranceProofPreview={state.setInsuranceProofPreview}
            onSubmit={handleInsuranceSubmit}
            isLoading={isInsuranceLoading}
          />
        </TabsContent>

        <TabsContent value="cpc-upload">
          <CPCSection
            cpcFrontFile={state.cpcFrontFile}
            setCpcFrontFile={state.setCpcFrontFile}
            cpcFrontPreview={state.cpcFrontPreview}
            setCpcFrontPreview={state.setCpcFrontPreview}
            cpcBackFile={state.cpcBackFile}
            setCpcBackFile={state.setCpcBackFile}
            cpcBackPreview={state.cpcBackPreview}
            setCpcBackPreview={state.setCpcBackPreview}
            onSubmit={handleCPCSubmit}
            isLoading={isCPCLoading}
          />
        </TabsContent>

        <TabsContent value="tacho-card-upload">
          <TachoCardSection
            tachoFrontFile={state.tachoFrontFile}
            setTachoFrontFile={state.setTachoFrontFile}
            tachoFrontPreview={state.tachoFrontPreview}
            setTachoFrontPreview={state.setTachoFrontPreview}
            tachoBackFile={state.tachoBackFile}
            setTachoBackFile={state.setTachoBackFile}
            tachoBackPreview={state.tachoBackPreview}
            setTachoBackPreview={state.setTachoBackPreview}
            tachoCardNumber={state.tachoCardNumber}
            setTachoCardNumber={state.setTachoCardNumber}
            onSubmit={handleTachoCardSubmit}
            isLoading={isTachoLoading}
          />
        </TabsContent>

        <TabsContent value="d4-medical-report-upload">
          <D4MedicalSection
            file={state.d4MedicalFile}
            setFile={state.setD4MedicalFile}
            preview={state.d4MedicalPreview}
            setPreview={state.setD4MedicalPreview}
            onSubmit={handleD4MedicalSubmit}
            isLoading={isD4Loading}
          />
        </TabsContent>
      </DocumentUploadTabs>
    </div>
  )
}

export default DocumentUpload
